import { ParsedData, StepProps } from '@betterment/js-runtime';
import { Button, Text } from '@betterment/samba-react';
import { FormikTextInput, Heading } from '@betterment/style-closet-react';
import { partial as _partial } from 'es-toolkit/compat';
import { Form, Formik, FormikHelpers } from 'formik';

import { PinResetParams } from '../Checking/data';
import { InputGroupWithLabelAndError, applyApiErrorsToFormik } from '../Checking/form-helpers';
import { validateConfirmPin, validateNewPin } from '../Checking/pin-validations';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import WorkflowLayout from '../Shared/flows/components/ConnectedWorkflowLayout';

const initialValues = {
  newPin: '',
  confirmPin: '',
};

interface Props extends StepProps {
  onInitialPinSetup: (data: PinResetParams) => Promise<ParsedData<{}>>;
}

export const PinSetup: React.FC<Props> = ({ onInitialPinSetup, goToNextStep }) => {
  const onSubmit = async (values: PinResetParams, formik: FormikHelpers<typeof initialValues>) => {
    formik.setStatus(null);
    try {
      const response: ParsedData<{}> = await onInitialPinSetup(values);

      if (response && response.errors) {
        applyApiErrorsToFormik(formik, response.errors);
      } else {
        goToNextStep();
      }
    } finally {
      formik.setSubmitting(false);
    }
  };

  return (
    <WorkflowLayout title="Activate your debit card">
      <WorkflowLayout.Slot>
        <Heading as="h1" fontSize={[5, 6]} mb="6">
          Set up your PIN
        </Heading>
        <Text
          as="p"
          css={{
            lineHeight: 'lineHeight.100',
            marginBottom: 'space.200',
          }}
        >
          It may take up to an hour for your new PIN to take effect. If you ever forget your PIN, you can reset it by
          logging into your account.
        </Text>
      </WorkflowLayout.Slot>
      <WorkflowLayout.Slot>
        <Formik<typeof initialValues>
          onSubmit={onSubmit}
          initialValues={initialValues}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ isSubmitting, values, validateField, setTouched, touched }) => (
            <Form>
              <FlashErrorMessage name="base" />
              <InputGroupWithLabelAndError fieldName="newPin" labelText="Create your 4-digit PIN">
                {(props: { id: string; name: string }) => (
                  <FormikTextInput
                    {...props}
                    type="password"
                    maxLength={4}
                    disabled={isSubmitting}
                    mb={3}
                    validate={validateNewPin}
                    onBlur={() => {
                      void setTouched({ ...touched, newPin: true });
                      void validateField('newPin');
                    }}
                  />
                )}
              </InputGroupWithLabelAndError>
              <InputGroupWithLabelAndError fieldName="confirmPin" labelText="Confirm your 4-digit PIN">
                {(props: { id: string; name: string }) => (
                  <FormikTextInput
                    {...props}
                    type="password"
                    maxLength={4}
                    disabled={isSubmitting}
                    mb={3}
                    validate={_partial(validateConfirmPin, values.newPin)}
                    onBlur={() => {
                      void setTouched({ ...touched, confirmPin: true });
                      void validateField('confirmPin');
                    }}
                  />
                )}
              </InputGroupWithLabelAndError>
              <Button type="submit" disabled={isSubmitting}>
                Create PIN
              </Button>
            </Form>
          )}
        </Formik>
      </WorkflowLayout.Slot>
    </WorkflowLayout>
  );
};
